'use client';

import React, { useEffect, useRef, useState } from 'react';
import { notFound } from 'next/navigation';
import { RichTextDisplay } from '@/components/ui/rich-text-display';
import { Badge } from '@/components/ui/badge';
import { Star, Calendar, ArrowLeft, Check } from 'lucide-react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { useCaseStudyBySlug } from '@/hooks/useCaseStudy';
import HomePageLayout from '@/components/layout/HomePageLayout';
import { ShareButton } from '@/components/ui/ShareButton';
import { PublicCaseStudyView } from '@/components/case-study/PublicCaseStudyView';
import { VerifiedBadge } from '@/components/designer-card/CardContent';
import { supabase } from '@/integrations/supabase/client';
import { recordCaseStudyView, canRecordCaseStudyView } from '@/services/caseStudy/recordCaseStudyView';

interface CaseStudyPageClientProps {
  slug: string;
}

export default function CaseStudyPageClient({ slug }: CaseStudyPageClientProps) {
  // Fetch case study by slug from Supabase
  const { data: caseStudy, isLoading, error } = useCaseStudyBySlug(slug);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isHeaderBgOpaque, setIsHeaderBgOpaque] = useState(false);
  const lastScrollY = useRef(0);
  const isTicking = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (!isTicking.current) {
        window.requestAnimationFrame(() => {
          const isScrollingDown = currentScrollY > lastScrollY.current;
          const hasPassedThreshold = currentScrollY > 120;

          if (!hasPassedThreshold) {
            setIsHeaderVisible(true);
            setIsHeaderBgOpaque(false);
          } else if (isScrollingDown) {
            setIsHeaderVisible(false);
            setIsHeaderBgOpaque(false);
          } else {
            setIsHeaderVisible(true);
            setIsHeaderBgOpaque(true);
          }

          lastScrollY.current = currentScrollY;
          isTicking.current = false;
        });

        isTicking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Track case study view when component loads
  useEffect(() => {
    if (caseStudy?.id && caseStudy?.profile_id) {
      // Get current user for viewer_id
      const trackView = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (canRecordCaseStudyView(caseStudy.id)) {
          recordCaseStudyView(
            caseStudy.profile_id,
            caseStudy.id,
            user?.id || null
          );
        }
      };
      trackView();
    }
  }, [caseStudy?.id, caseStudy?.profile_id]);

  if (isLoading) {
    return (
      <HomePageLayout>
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading case study...</p>
          </div>
        </div>
      </HomePageLayout>
    );
  }

  if (!caseStudy) {
    notFound();
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const caseStudyUrl = `/case-study/${caseStudy.slug || caseStudy.id}`;
  const profileUrl = caseStudy.profile?.username_slug ? `/u/${caseStudy.profile.username_slug}` : '/u/designer';

  return (
    <HomePageLayout>
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className={`fixed right-0 left-0 top-[64px] z-10 px-6 py-5 md:py-6 bg-gray-100 ${isHeaderBgOpaque ? 'bg-opacity-100' : 'bg-opacity-60'} transition-all duration-300 ease-out lg:backdrop-blur- items-center ${isHeaderVisible ? 'translate-y-0' : '-translate-y-full'} shadow-[inset_0px_4px_7px_-5px_rgba(0,0,0,0.3)]`}>
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex md:justify-between md:items-center gap-4">
                <Link href={profileUrl}>
                  <ArrowLeft className="h-4 w-4 md:mr-2" />
                </Link>
                <div className="flex items-center text-xs gap-2 text-gray-600">
                  <Link
                    href={profileUrl}
                    className="text-gray-500 hidden md:block hover:text-[#D2622A] transition-colors duration-200 cursor-pointer"
                  >
                    {caseStudy.profile?.display_name || caseStudy.profile?.username}
                  </Link>
                  {caseStudy.profile?.is_featured && (
                    <VerifiedBadge width={16} height={16} />
                  )}
                  <span className="text-gray-400 hidden md:block">
                    <img src="/chevron.svg" alt="" className="h-2 w-2"/>
                  </span>
                  <Link
                    href={profileUrl}
                    className="text-gray-500 hidden md:block hover:text-[#D2622A] transition-colors duration-200 cursor-pointer"
                  >
                    Case study
                  </Link>
                  <span className="text-gray-400 hidden md:block">
                    <img src="/chevron.svg" alt=""className="h-2 w-2"/>
                  </span>
                  <span className="font-semibold text-gray-900">{caseStudy.title}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto px-6 py-8 pt-24 md:pt-32">
          <PublicCaseStudyView caseStudy={caseStudy} />
        </div>
      </div>
    </HomePageLayout>
  );
}
